/**
 * DeleteAccountSection.jsx — RC 1.44 / P0.4
 *
 * High-friction self-service account deletion. Single-tile
 * "Danger zone" component rendered on the Settings tab.
 *
 * Friction strategy (per directive):
 *   1. Section is collapsed by default behind a Danger Zone heading.
 *   2. "Delete account" button opens a confirmation dialog.
 *   3. Dialog requires the user to type the literal word "DELETE"
 *      into a confirmation input AND tick the consequence
 *      acknowledgement checkbox before the final destructive
 *      button enables.
 *   4. Final delete button is RED, labelled
 *      "Permanently delete my account".
 *   5. On 200, the local Supabase session is signed out and the
 *      user is hard-redirected to `/`.
 *   6. On 403 OPERATOR_PROTECTED, a toast surfaces the
 *      "operator account is protected" copy and the dialog stays
 *      open.
 *
 * Honest, irreversible, single-purpose. No undo, no soft-delete.
 */
import { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { AlertTriangle, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { apiFetch } from '@/lib/api';
import { supabase } from '@/lib/supabase';

const CONFIRM_WORD = 'DELETE';

export default function DeleteAccountSection() {
  const [open, setOpen]               = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [ack, setAck]                 = useState(false);
  const [busy, setBusy]               = useState(false);

  const canDelete = useMemo(
    () => confirmText.trim().toUpperCase() === CONFIRM_WORD && ack && !busy,
    [confirmText, ack, busy],
  );

  const reset = () => {
    setConfirmText('');
    setAck(false);
    setBusy(false);
  };

  const onDelete = async () => {
    if (!canDelete) return;
    setBusy(true);
    try {
      await apiFetch('/api/account', { method: 'DELETE' });
      // Successful — drop the session and hard-redirect home.
      try { await supabase.auth.signOut(); } catch { /* ignore */ }
      try {
        // Clear any local artefacts that might re-create flicker.
        Object.keys(localStorage)
          .filter((k) => k.startsWith('ndor:') || k.startsWith('sb-'))
          .forEach((k) => localStorage.removeItem(k));
      } catch { /* ignore */ }
      toast.success('Your account has been deleted. Goodbye.');
      // setTimeout to let the toast render before navigation.
      setTimeout(() => { window.location.assign('/'); }, 600);
    } catch (err) {
      const code = err?.data?.detail?.code;
      const msg  = err?.data?.detail?.message || err?.data?.detail || err?.message;
      if (code === 'OPERATOR_PROTECTED') {
        toast.error(typeof msg === 'string' ? msg : 'This account is protected and cannot be deleted.');
        // Stay open so the user sees the friction reason.
      } else {
        toast.error(typeof msg === 'string' ? msg : 'Account deletion failed. Please try again.');
      }
      setBusy(false);
    }
  };

  return (
    <Card
      className="border-red-200 bg-red-50/30 shadow-sm"
      data-testid="delete-account-section"
    >
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base font-medium text-rose-200">
          <AlertTriangle className="h-5 w-5" />
          Danger zone
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-ndor-ink-secondary leading-relaxed">
          Deleting your account permanently removes your CVs, generated
          variants, application packs, cover letters, preferences,
          credit balance, plan history, and all consents.
          <strong className="text-rose-200"> This cannot be undone.</strong>
          {' '}Your Stripe billing history is retained by Stripe under
          their independent retention policy; consult our Privacy Policy
          for the full data list.
        </p>

        <AlertDialog
          open={open}
          onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}
        >
          <AlertDialogTrigger asChild>
            <Button
              variant="destructive"
              className="bg-red-600 hover:bg-red-700"
              data-testid="delete-account-open-btn"
            >
              <Trash2 className="h-4 w-4 mr-1.5" />
              Delete my account
            </Button>
          </AlertDialogTrigger>

          <AlertDialogContent
            className="sm:max-w-md"
            data-testid="delete-account-confirm-dialog"
          >
            <AlertDialogHeader>
              <AlertDialogTitle className="text-rose-200 inline-flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Permanently delete your account?
              </AlertDialogTitle>
              <AlertDialogDescription className="text-sm leading-relaxed">
                Every CV, variant, pack, cover letter, preference,
                credit balance and consent record tied to this account
                will be removed from NDORFlow. You will be signed out
                immediately and will not be able to recover your data.
              </AlertDialogDescription>
            </AlertDialogHeader>

            <div className="space-y-3 py-2">
              <div className="space-y-1.5">
                <Label
                  htmlFor="delete-account-confirm-input"
                  className="text-xs text-ndor-ink"
                >
                  Type <code className="font-mono text-rose-200">{CONFIRM_WORD}</code> to enable the delete button:
                </Label>
                <Input
                  id="delete-account-confirm-input"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder={CONFIRM_WORD}
                  autoComplete="off"
                  className="font-mono"
                  data-testid="delete-account-confirm-input"
                />
              </div>

              <label
                className="flex items-start gap-2 text-xs text-ndor-ink-secondary leading-relaxed cursor-pointer"
                data-testid="delete-account-ack-row"
              >
                <Checkbox
                  checked={ack}
                  onCheckedChange={(v) => setAck(Boolean(v))}
                  data-testid="delete-account-ack-checkbox"
                />
                <span>
                  I understand this is permanent and irreversible, and
                  that NDORFlow cannot recover my data once deleted.
                </span>
              </label>
            </div>

            <AlertDialogFooter className="gap-2">
              <AlertDialogCancel data-testid="delete-account-cancel-btn">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={onDelete}
                disabled={!canDelete}
                className="bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="delete-account-confirm-btn"
              >
                {busy && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
                Permanently delete my account
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}
